import {
  Box,
  Stack,
  Typography,
  Toolbar,
  Container,
  Collapse,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { useOrdering } from 'hooks'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { FailureIcon } from 'components/images'
import { Background2 } from 'components/backgrounds/Background2'
import { KBottomAppBar } from 'components/core/KBottomAppBar'
import { BottomBarActionButton } from './BottomBarActionButton'

interface SubmitOrderFailureState {
  message: string
  detail: string
  showDetail: boolean
}

interface SubmitOrderFailureProps {
  tryAgainUrl: string
}

export const SubmitOrderFailure = ({
  tryAgainUrl,
}: SubmitOrderFailureProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    submitOrderState: { error },
    clearSubmitOrderState,
  } = useOrdering()

  const [state, setState] = useState<SubmitOrderFailureState>({
    message: '',
    detail: '',
    showDetail: false,
  })

  useEffect(() => {
    setState({
      message: error?.message ?? t('Something went wrong.'),
      detail: error?.detail ?? '',
      showDetail: false,
    })
  }, [error, t])

  const handleTryAgain = () => {
    clearSubmitOrderState()
    navigate(tryAgainUrl)
  }

  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <Background2 sx={{ flexGrow: 1, overflowY: 'auto', pt: 4, pb: 4 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              borderRadius: 4,
              backgroundColor: 'background.paper',
            }}
          >
            <Stack
              sx={{
                margin: 'auto',
                gap: 2,
                alignItems: 'stretch',
                padding: 4,
                maxWidth: 560,
              }}
            >
              <Box sx={{ alignSelf: 'center' }}>
                <FailureIcon width={isDownSm ? 120 : 160} />
              </Box>

              <Typography
                variant="h4"
                color="secondary"
                sx={{ alignSelf: 'center', maxWidth: '100%' }}
              >
                <Trans defaults={state.message} />
              </Typography>
              {state.detail && (
                <Button
                  type="text"
                  href="#"
                  onClick={() =>
                    setState({ ...state, showDetail: !state.showDetail })
                  }
                >
                  {state.showDetail ? t('Hide Detail') : t('Show Detail')}
                </Button>
              )}
              <Collapse in={state.showDetail}>
                <Typography
                  variant="body2"
                  color="secondary"
                  sx={{ alignSelf: 'center', maxWidth: '100%' }}
                >
                  {state.detail}
                </Typography>
              </Collapse>
            </Stack>
          </Box>
        </Container>
      </Background2>
      <KBottomAppBar>
        <Toolbar disableGutters>
          <Box sx={{ flexGrow: 1 }} />
          <BottomBarActionButton onClick={handleTryAgain}>
            {t('Try Again')}
          </BottomBarActionButton>
        </Toolbar>
      </KBottomAppBar>
    </Stack>
  )
}
